import React, { useState, useEffect, useCallback } from "react";
import Cropper from "react-easy-crop";
import { RotateCw, RotateCcw, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { getProcessedImage } from "@/lib/cropImage";

export function ImageEditor({ open, imageSrc, onCancel, onSave }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [pixels, setPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setBrightness(100);
      setContrast(100);
      setPixels(null);
      setSaving(false);
    }
  }, [open, imageSrc]);

  const onCropComplete = useCallback((_, p) => setPixels(p), []);

  const reset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setBrightness(100);
    setContrast(100);
  };

  const save = async () => {
    if (!pixels) return;
    setSaving(true);
    try {
      const blob = await getProcessedImage(imageSrc, pixels, rotation, brightness, contrast);
      await onSave(blob);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onCancel(); }}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="image-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl tracking-tight">Edit Image</DialogTitle>
          <DialogDescription>Crop, rotate and adjust before saving. Exported as an 800×800 square.</DialogDescription>
        </DialogHeader>

        <div className="relative w-full h-72 bg-neutral-900 rounded-sm overflow-hidden" data-testid="image-editor-canvas">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              restrictPosition={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              style={{ mediaStyle: { filter: `brightness(${brightness}%) contrast(${contrast}%)` } }}
            />
          )}
        </div>

        <div className="space-y-4 mt-1">
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" size="sm" className="rounded-sm gap-1" onClick={() => setRotation((r) => (r - 90 + 360) % 360)} data-testid="editor-rotate-left">
              <RotateCcw className="h-4 w-4" strokeWidth={1.5} /> 90°
            </Button>
            <Button type="button" variant="outline" size="sm" className="rounded-sm gap-1" onClick={() => setRotation((r) => (r + 90) % 360)} data-testid="editor-rotate-right">
              <RotateCw className="h-4 w-4" strokeWidth={1.5} /> 90°
            </Button>
            <Button type="button" variant="ghost" size="sm" className="rounded-sm gap-1 ml-auto text-muted-foreground" onClick={reset} data-testid="editor-reset">
              <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} /> Reset
            </Button>
          </div>

          <div className="space-y-2">
            <Label className="overline flex justify-between"><span>Zoom</span><span className="text-muted-foreground">{zoom.toFixed(1)}×</span></Label>
            <Slider value={[zoom]} min={1} max={3} step={0.01} onValueChange={(v) => setZoom(v[0])} data-testid="editor-zoom-slider" />
          </div>
          <div className="space-y-2">
            <Label className="overline flex justify-between"><span>Brightness</span><span className="text-muted-foreground">{brightness}%</span></Label>
            <Slider value={[brightness]} min={50} max={150} step={1} onValueChange={(v) => setBrightness(v[0])} data-testid="editor-brightness-slider" />
          </div>
          <div className="space-y-2">
            <Label className="overline flex justify-between"><span>Contrast</span><span className="text-muted-foreground">{contrast}%</span></Label>
            <Slider value={[contrast]} min={50} max={150} step={1} onValueChange={(v) => setContrast(v[0])} data-testid="editor-contrast-slider" />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="rounded-sm" onClick={onCancel} disabled={saving} data-testid="editor-cancel">Cancel</Button>
          <Button className="rounded-sm bg-foreground text-background hover:bg-foreground/90" onClick={save} disabled={saving || !pixels} data-testid="editor-save">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Saving…</> : "Save Image"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ImageEditor;
